import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRightIcon, ChevronLeftIcon, ChevronRightIcon, Maximize2Icon, XIcon } from "lucide-react";
const CAREERS_URL = "https://bigyellow.au/careers/";
type CrewPhoto = {
  src: string;
  alt: string;
  position: string;
  feature?: boolean;
};

/*
  Grid is built to always form a clean rectangle:
  - Mobile (2 cols) / tablet (3 cols): 18 equal squares → 9 or 6 full rows.
  - Desktop (4 cols): 16 squares + 2 double-size features = 24 cells → 6 full rows.
  Keep the order below: features sit at item 1 and item 11 so auto-placement fills every cell.
*/
const photos: CrewPhoto[] = [{
  src: "https://cdn.magicpatterns.com/uploads/3eptXLYTS93TAbnik3TDuQ/1696-45d40a7b-dc0f-404d-8781-2034dca838a6-HarveyHARRISON_1787308772295.jpg",
  alt: "Excavator loading a Big Yellow haul truck under a big cloudy sky",
  position: "object-[55%_70%]",
  feature: true
}, {
  src: "https://cdn.magicpatterns.com/uploads/sg7EcN82imAB1XqXmGt1uR/1696-71eb519d-5ddf-49dc-81d3-a0a17844ebd1-DamianHARRISON_1787984397715.jpg",
  alt: "Big Yellow haul truck lit up on the pad at night",
  position: "object-[50%_45%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/mVyiV3psK8b43XGZ5AbofW/1696-4bb1959b-f53e-4453-9b2f-c1937d939e75-NathanBOWMAN_1787309692548.jpg",
  alt: "Drill rigs working the floor of an open-cut pit",
  position: "object-[55%_85%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/bfJTPV3qSwAte3S1LhQ9DM/1696-dd7d33df-f47a-4390-9be6-01b4c5e4321a-DamianHARRISON_1788532798699.jpg",
  alt: "Big Yellow haul truck parked on red earth under a clear blue sky",
  position: "object-[50%_60%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/nZi1WprC7Lz8MP3p6Wi6L9/1696-a9ca49c5-58db-4923-8b5a-8b41eaf9bfce-LeighCOXALL_1787724859186.jpg",
  alt: "Crew member at a Big Yellow haul truck beside the muster point at dawn",
  position: "object-[15%_70%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/bicDGRcCut2V8muCs3pdWj/1696-7118e7bd-a7bd-4a43-add5-858b1fb9474a-RachaelSEALEY_1788167476730.jpg",
  alt: "Excavator under work lights beneath a full moon",
  position: "object-center"
}, {
  src: "https://cdn.magicpatterns.com/uploads/sL3ZhV2o5hJKq8inuLCBTV/1696-7ee84dd0-6ded-4f7c-afec-92aa6fd126b5-AaronWATT_1790216272421.jpg",
  alt: "Excavator loading a haul truck in early-morning haze",
  position: "object-[55%_40%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/nA32GFgrpAUkDq7VZUXvju/1696-21b3ecc0-8f27-461a-952a-440184475879-SiobhanMURPHY_1788726121375.jpg",
  alt: "Big Yellow grader under a moonlit sky",
  position: "object-[50%_55%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/mtLkTDF5Dj98ZBwKE4aove/1696-37468442-417f-4406-8b21-c257aba8588a-JamesKELLAHAN_1788417258637.jpg",
  alt: "Haul truck tipping a load under a big blue sky",
  position: "object-[50%_42%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/78rgR4zaSRHvAycfLQVRCu/1696-3cfc917b-488e-41ce-b73a-ee461210fb3b-BrittanyWINTER_1787481269311.jpg",
  alt: "Big Yellow haul truck under lights on night shift",
  position: "object-center"
}, {
  src: "https://cdn.magicpatterns.com/uploads/iNyzBKhNSNuWphkFzxU16n/Aladdin_pit_June_26.jpg",
  alt: "Excavator working an open-cut pit at sunset",
  position: "object-[38%_60%]",
  feature: true
}, {
  src: "https://cdn.magicpatterns.com/uploads/wayUbeJ4aDbs85UGXjsQST/1696-0cb6b62d-eed1-453e-975e-e632cee4c552-DarioDOMECQ_1788167552901.jpg",
  alt: "A full moon rising, seen from beneath a haul truck tray",
  position: "object-[50%_40%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/4LEapiUhdtuw4PSWz6bubZ/1696-e7c8c91e-0b53-4990-bd89-b184ce97f30f-DaleNISBET_1788948295981.jpg",
  alt: "Track dozer at first light",
  position: "object-center"
}, {
  src: "https://cdn.magicpatterns.com/uploads/7oDa64rckKD3KHW27SZso8/Go_Line_Freight_Truck_1.png",
  alt: "Big Yellow freight truck on the go line beside the haul fleet",
  position: "object-[62%_55%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/2rzLXRQraCADD2ExGUZknc/1696-fefc9307-2d1e-43f2-bbdf-0ba9587b6cfe-AngelaPENWRIGHT_1790060281537.jpg",
  alt: "A line of Big Yellow haul trucks parked up at dusk",
  position: "object-[50%_85%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/4mrqBuri6krwfV4GQHTNBR/1696-980b731d-6f2a-4d25-806e-7713c9dcb975-HarveyHARRISON_1787308772342.jpg",
  alt: "Excavator digging under lights at night",
  position: "object-[40%_60%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/qX54yzUNodaHcyJeSvRzZP/1696-636baf1f-c869-4f66-aaf6-6c0e59298baf-LoganMURDIE_1788864160578.jpg",
  alt: "Dusk over a salt lake near site",
  position: "object-[50%_55%]"
}, {
  src: "https://cdn.magicpatterns.com/uploads/7YJwBdBHeyqMqq9BwMZnHQ/1696-e09d24d4-571c-485d-90b4-4ddea0ed96b5-HarveyHARRISON_1787308772391.jpg",
  alt: "Big Yellow light vehicles under an orange storm sky",
  position: "object-[50%_75%]"
}];
export function CrewGallery() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const open = activeIndex !== null;
  const show = (index: number) => {
    triggerRef.current = document.activeElement as HTMLElement | null;
    setActiveIndex(index);
  };
  const close = useCallback(() => setActiveIndex(null), []);
  const step = useCallback((direction: number) => {
    setActiveIndex(current => current === null ? current : (current + direction + photos.length) % photos.length);
  }, []);
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "Tab" && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>("button"));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
      triggerRef.current?.focus();
    };
  }, [open, close, step]);
  const active = activeIndex !== null ? photos[activeIndex] : null;
  const controlClass = "inline-flex h-12 w-12 items-center justify-center border border-white/40 text-white transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-white hover:text-black active:scale-95";
  return <section id="crew" aria-labelledby="crew-heading" className="bg-white text-black" data-id="element-166" data-mp="1qixkdmbvgoad">
      <div className="mx-auto w-full max-w-[1600px] px-6 py-[3.75rem] md:py-[5.25rem] lg:px-12 xl:px-20" data-id="element-167" data-mp="1undobi1xdlmy">
        <div className="grid gap-8 md:grid-cols-12 md:items-end" data-id="element-168" data-mp="1gnvtom1y7e5o">
          <div className="md:col-span-7" data-id="element-169" data-mp="1lkckxp4g28mz">
            <p className="mb-5 text-[0.8125rem] font-semibold uppercase tracking-[0.14em] text-[#005A73]" data-id="element-170" data-mp="1ln8g55owqj7q">Life on site</p>
            <h2 id="crew-heading" className="text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-5xl lg:text-7xl" data-id="element-171" data-mp="2e9tggvnd25nf">Through our crew&apos;s eyes.</h2>
          </div>
          <p className="max-w-lg text-lg leading-8 text-black/75 md:col-span-5 md:col-start-8" data-id="element-172" data-mp="1zknc6t9o1p8o">Dawn starts, night shifts and big WA skies. Every photo here was taken by Big Yellow crews on site.</p>
        </div>

        <ul className="mt-14 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-flow-row-dense lg:grid-cols-4 lg:gap-4" data-id="element-173" data-mp="2s7rjx6058ho5">
          {photos.map((photo, index) => <li key={photo.src} className={photo.feature ? "lg:col-span-2 lg:row-span-2" : ""} data-id="element-174" data-mp="wzmgw1mh4n6a" data-mpi={index}>
              <button type="button" onClick={() => show(index)} aria-label={`View larger: ${photo.alt}`} className={`group relative block w-full overflow-hidden bg-black ${photo.feature ? "aspect-square lg:aspect-auto lg:h-full" : "aspect-square"}`} data-id="element-175" data-mp="s5s0b7260obf">
                <img src={photo.src} alt={photo.alt} loading="lazy" className={`absolute inset-0 h-full w-full object-cover ${photo.position} transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-[1.04]`} data-id="element-176" data-mp="1fwnlvmg12na8" />
                <span aria-hidden="true" className="absolute bottom-3 right-3 inline-flex h-10 w-10 items-center justify-center bg-black/75 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100" data-id="element-177" data-mp="16uwhqm14yoih">
                  <Maximize2Icon size={17} data-id="element-178" data-mp="3mndfl1sfgcbi" />
                </span>
              </button>
            </li>)}
        </ul>

        <div className="mt-10 flex flex-col gap-6 border-t border-black/20 pt-8 lg:flex-row lg:items-center lg:justify-between" data-id="element-179" data-mp="22648ngzopet4">
          <div data-id="element-180" data-mp="274ntfe2jkkjw">
            <p className="text-xl font-semibold" data-id="element-181" data-mp="272bb2zsvg86b">Picture yourself on the next shift?</p>
            <p className="mt-1 text-base text-black/70" data-id="element-182" data-mp="2b4pxftvr1q2c">Tell us where you fit, or check the roles that are open now.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row" data-id="element-183" data-mp="2kw2o4i5taa6s">
            <button type="button" data-open-eoi className="btn btn-primary btn-sm" data-id="element-184" data-mp="2kvkl92on8xpw">
              Register your interest <ArrowUpRightIcon aria-hidden="true" size={17} className="btn-icon" data-id="element-185" data-mp="2px04cp79gu5n" />
            </button>
            <a href={CAREERS_URL} className="btn btn-ghost-dark btn-sm" data-id="element-186" data-mp="2urw1u4v9o9q5">
              View opportunities <ArrowUpRightIcon aria-hidden="true" size={17} className="btn-icon" data-id="element-187" data-mp="2ythhtz33l44k" />
            </a>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-188" data-mp="14h12m8sbqfnk">
        {active && activeIndex !== null && <motion.div key="lightbox" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Crew photo viewer" className="fixed inset-0 z-[90] flex flex-col bg-black/95 text-white" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onMouseDown={event => event.target === event.currentTarget && close()} data-id="element-189" data-mp="14jdt5ac5dyc8">
            <div className="flex items-center justify-between px-4 py-4 sm:px-8" data-id="element-190" data-mp="2qkrc8u0yvnwc">
              <p className="text-sm font-medium text-white/70" aria-live="polite" data-id="element-191" data-mp="2vcj6pfn8udrr">{activeIndex + 1} / {photos.length}</p>
              <button ref={closeRef} type="button" onClick={close} aria-label="Close photo viewer" className={controlClass} data-id="element-192" data-mp="30bm022tv2vjw">
                <XIcon aria-hidden="true" size={22} data-id="element-193" data-mp="35c08vn3ae9iz" />
              </button>
            </div>
            <figure className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 sm:px-20" onMouseDown={event => event.target === event.currentTarget && close()} data-id="element-194" data-mp="3aabka298cs3b">
              <img src={active.src} alt={active.alt} className="max-h-[72vh] w-auto max-w-full object-contain" data-id="element-195" data-mp="tmu0lsdl3nmz" />
              <figcaption className="mt-4 max-w-2xl text-center text-base text-white/85" data-id="element-196" data-mp="oos86gu9qbyu">{active.alt}</figcaption>
            </figure>
            <div className="flex items-center justify-center gap-3 px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-4" data-id="element-197" data-mp="jr0jdvox75yv">
              <button type="button" onClick={() => step(-1)} aria-label="Previous photo" className={controlClass} data-id="element-198" data-mp="1cdv0cimj3n7l">
                <ChevronLeftIcon aria-hidden="true" size={22} data-id="element-199" data-mp="17md0xz05luoq" />
              </button>
              <button type="button" onClick={() => step(1)} aria-label="Next photo" className={controlClass} data-id="element-200" data-mp="12o1m19v04tzn">
                <ChevronRightIcon aria-hidden="true" size={22} data-id="element-201" data-mp="203p84lrzqlkz" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}